// Client portal Exports tab — read-only duty + tariff fees.
// Source badge follows the same classifier as the admin ExportsTab so a
// FedEx duty-PDF-ingested row reads as "FedEx", not "Zonos".

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { getMeExports } from '../../../lib/api'
import {
  formatCurrency, formatDate,
  currentMonthStart, todayStr, DateRangeFilter,
} from './helpers'

function classifySource(f) {
  if (f.manual) return { label: 'Manual', cls: 'bg-violet-100 text-violet-700' }
  if ((f.courier || '').toLowerCase() === 'fedex' && !f.zonos_order_id) {
    return { label: 'FedEx', cls: 'bg-orange-100 text-orange-700' }
  }
  return { label: 'Zonos', cls: 'bg-blue-100 text-blue-700' }
}

export default function ExportsTab({ clientId }) {
  const [from, setFrom] = useState(currentMonthStart())
  const [to,   setTo]   = useState(todayStr())

  const params = useMemo(() => ({
    ...(from && { from }),
    ...(to   && { to   }),
  }), [from, to])

  const { data: fees = [], isLoading, isError, error } = useQuery({
    queryKey: ['me', 'exports', clientId, params],
    queryFn:  () => getMeExports(clientId, params),
    enabled:  !!clientId,
  })

  const sourceTotals = useMemo(() => {
    const out = { zonos: 0, fedex: 0, manual: 0 }
    for (const f of fees) {
      const key = f.manual
        ? 'manual'
        : (f.courier || '').toLowerCase() === 'fedex' && !f.zonos_order_id
          ? 'fedex'
          : 'zonos'
      out[key] += Number(f.total_amount) || 0
    }
    return out
  }, [fees])
  const grandTotal = fees.reduce((s, f) => s + (Number(f.total_amount) || 0), 0)
  const primaryCurrency = fees[0]?.currency || 'AUD'

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Export duties &amp; tariffs</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Per-order duty, tax and processing fees collected from Zonos and FedEx.
            </p>
          </div>
          <DateRangeFilter from={from} to={to} onChange={({ from: f, to: t }) => { setFrom(f); setTo(t) }} />
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : isError ? (
          // Failure banner — empty-state copy for a fetch error implied
          // "no exports this period", which is materially different.
          <div className="p-4 text-sm text-red-700 bg-red-50 border-t border-red-200 flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <span>Failed to load: {error?.response?.data?.detail || error?.message || 'Unknown error'}</span>
          </div>
        ) : fees.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">No export fees in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-3 py-2.5">Date</th>
                  <th className="px-3 py-2.5">Order #</th>
                  <th className="px-3 py-2.5">Tracking</th>
                  <th className="px-3 py-2.5">Source</th>
                  <th className="px-3 py-2.5">Currency</th>
                  <th className="px-3 py-2.5 text-right">Duty</th>
                  <th className="px-3 py-2.5 text-right">Tax</th>
                  <th className="px-3 py-2.5 text-right">Fees</th>
                  <th className="px-3 py-2.5 text-right">Shipping</th>
                  <th className="px-3 py-2.5 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {fees.map((f, i) => {
                  const src = classifySource(f)
                  return (
                    <tr key={f.id || i} className={i % 2 === 1 ? 'bg-slate-50/40' : ''}>
                      <td className="px-3 py-2 text-slate-600">{formatDate(f.fee_date)}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">{f.order_number || '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{f.tracking_number || '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${src.cls}`}>{src.label}</span>
                      </td>
                      <td className="px-3 py-2 text-slate-500 uppercase text-xs">{f.currency || 'AUD'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatCurrency(f.duty_amount, f.currency)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatCurrency(f.tax_amount, f.currency)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatCurrency(f.fee_amount, f.currency)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatCurrency(f.shipping_amount, f.currency)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-800">{formatCurrency(f.total_amount, f.currency)}</td>
                    </tr>
                  )
                })}
                {/* Source subtotal strip */}
                {(sourceTotals.zonos + sourceTotals.fedex + sourceTotals.manual) > 0 && (
                  <tr className="bg-slate-50/60 border-t border-slate-200">
                    <td colSpan={9} className="px-3 py-2 text-right text-xs text-slate-500">
                      <span className="inline-flex items-center gap-3 flex-wrap justify-end">
                        {sourceTotals.zonos > 0  && <span>● Zonos  <span className="text-slate-700 tabular-nums">{formatCurrency(sourceTotals.zonos, primaryCurrency)}</span></span>}
                        {sourceTotals.fedex > 0  && <span>● FedEx  <span className="text-slate-700 tabular-nums">{formatCurrency(sourceTotals.fedex, primaryCurrency)}</span></span>}
                        {sourceTotals.manual > 0 && <span>● Manual <span className="text-slate-700 tabular-nums">{formatCurrency(sourceTotals.manual, primaryCurrency)}</span></span>}
                      </span>
                    </td>
                    <td />
                  </tr>
                )}
                <tr className="bg-slate-50 border-t-2 border-slate-200">
                  <td colSpan={9} className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Total ({fees.length} record{fees.length !== 1 ? 's' : ''})
                  </td>
                  <td className="px-3 py-2.5 text-right font-bold text-slate-800 tabular-nums">{formatCurrency(grandTotal, primaryCurrency)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
